import React, { useEffect, useState } from 'react';

import { checkViews, toAbsoluteUrl } from '../services/extensionBridge';
import {
  ClaimResult,
  extractApiError,
  formatCountdown,
  parseUtcMs,
  promotionService,
  ShareInfo,
  ShareItem,
} from '../services/promotionService';

import { PortraitReelPreview } from './PortraitReelPreview';

const COUNTDOWN_REFRESH_MS = 30000;

export type ShareWorkspace = 'create' | 'history';

const useHistoryNow = (): number => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), COUNTDOWN_REFRESH_MS);

    return () => window.clearInterval(timer);
  }, []);

  return now;
};

const claimedViewsLabel = (share: ShareItem): string => (
  share.link_deleted
    ? 'Post link was unavailable · no view credits'
    : `${share.views_reported} views · +${share.view_credits_awarded} credits claimed`
);

const readAndClaimViews = async (share: ShareItem): Promise<ClaimResult> => {
  const check = await checkViews({
    shareId: share.id,
    platform: share.platform,
    reelUrl: share.reel_url || '',
  });

  if (!check.ok) throw new Error(check.error || 'The extension could not read this post’s view count.');

  return promotionService.claimShareViews(share.id, check.views ?? 0, check.linkAlive !== false);
};

const claimButtonLabel = (
  installed: boolean,
  share: ShareItem,
  checking: boolean,
  unlocked: boolean,
  now: number,
): string => {
  if (!installed) return 'Install extension to claim views';
  if (!share.reel_url) return 'Post link required to claim views';
  if (checking) return 'Checking views in background…';
  if (unlocked) return 'Claim extra view credits';
  if (share.claimable_at) return `Claim view credits ${formatCountdown(share.claimable_at, now)}`;

  return 'Claim unavailable';
};

interface ViewClaimControlProps {
  installed: boolean;
  isSystemAdmin: boolean;
  refresh: () => void;
  share: ShareItem;
}

interface ClaimControlViewProps extends ViewClaimControlProps {
  checking: boolean;
  disabled: boolean;
  error: string | null;
  now: number;
  onClaim: () => void;
  onUnlock: () => void;
  result: ClaimResult | null;
  unlocking: boolean;
  unlocked: boolean;
}

const ClaimControlView: React.FC<ClaimControlViewProps> = (props) => {
  if (props.result) return <div className="promo-claim-result">{claimedViewsLabel(props.result)}</div>;
  if (props.share.views_claimed) return <div className="promo-claim-result">{claimedViewsLabel(props.share)}</div>;

  return (
    <div className="promo-claim-control">
      <button className="promo-claim-btn" disabled={props.disabled} onClick={props.onClaim} type="button">
        {claimButtonLabel(props.installed, props.share, props.checking, props.unlocked, props.now)}
      </button>
      {props.isSystemAdmin && !props.unlocked && Boolean(props.share.claimable_at) && (
        <button
          className="promo-claim-unlock-btn"
          disabled={props.unlocking}
          onClick={props.onUnlock}
          type="button"
        >
          {props.unlocking ? 'Unlocking…' : 'Unlock claim now (admin)'}
        </button>
      )}
      {props.error && <div className="promo-error">{props.error}</div>}
    </div>
  );
};

interface ClaimFlowState {
  adminUnlocked: boolean;
  checking: boolean;
  error: string | null;
  result: ClaimResult | null;
  unlocking: boolean;
}

type SetClaimFlow = React.Dispatch<React.SetStateAction<ClaimFlowState>>;

const executeClaim = (share: ShareItem, refresh: () => void, setFlow: SetClaimFlow) => {
  setFlow((current) => ({ ...current, checking: true, result: null, error: null }));
  void readAndClaimViews(share)
    .then((claimed) => {
      setFlow((current) => ({ ...current, checking: false, result: claimed, error: null }));
      refresh();
    })
    .catch((err) => setFlow((current) => ({
      ...current,
      checking: false,
      result: null,
      error: extractApiError(err, err instanceof Error ? err.message : 'Could not claim credits.'),
    })));
};

const executeUnlock = (shareId: string, refresh: () => void, setFlow: SetClaimFlow) => {
  setFlow((current) => ({ ...current, unlocking: true, error: null }));
  void promotionService.unlockShareViewClaim(shareId)
    .then(() => {
      setFlow((current) => ({ ...current, adminUnlocked: true, unlocking: false, error: null }));
      refresh();
    })
    .catch((err) => setFlow((current) => ({
      ...current,
      unlocking: false,
      error: extractApiError(err, 'Could not unlock the claim timer.'),
    })));
};

export const ViewClaimControl: React.FC<ViewClaimControlProps> = ({ installed, isSystemAdmin, refresh, share }) => {
  const now = useHistoryNow();
  const [flow, setFlow] = useState<ClaimFlowState>({
    adminUnlocked: false,
    checking: false,
    result: null,
    error: null,
    unlocking: false,
  });
  const unlocked = Boolean(
    !share.views_claimed
    && (
      share.claimable
      || flow.adminUnlocked
      || (share.claimable_at && parseUtcMs(share.claimable_at) <= now)
    ),
  );
  const disabled = !installed || !share.reel_url || !unlocked || flow.checking;

  const handleClaim = () => {
    if (disabled) return;
    executeClaim(share, refresh, setFlow);
  };

  const handleUnlock = () => {
    if (!isSystemAdmin || flow.unlocking || unlocked) return;
    executeUnlock(share.id, refresh, setFlow);
  };

  return (
    <ClaimControlView
      checking={flow.checking}
      disabled={disabled}
      error={flow.error}
      installed={installed}
      isSystemAdmin={isSystemAdmin}
      now={now}
      onClaim={handleClaim}
      onUnlock={handleUnlock}
      refresh={refresh}
      result={flow.result}
      share={share}
      unlocking={flow.unlocking}
      unlocked={unlocked}
    />
  );
};

const STATUS_LABELS: Record<string, string> = {
  pending_video: 'Generating',
  video_ready: 'Ready to post',
  posted: 'Posted',
  credited: 'Credited',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const platformLabel = (platform: string): string => (platform === 'linkedin' ? 'LinkedIn' : 'Instagram');

const HistoryRowHeader: React.FC<{ posted: boolean; share: ShareItem }> = ({ posted, share }) => {
  const projectLabel = (share.project_name || '').trim() || 'Reel';
  const viewCredits = share.view_credits_awarded;

  return (
    <div className="promo-share-row">
      <span className="promo-share-project" title={projectLabel}>{projectLabel}</span>
      {posted && (
        <span className={`promo-share-platform promo-share-platform--${share.platform}`}>
          {platformLabel(share.platform)}
        </span>
      )}
      <span className={`promo-status promo-status--${share.status}`}>
        {STATUS_LABELS[share.status] || share.status}
      </span>
      {viewCredits > 0 && <span className="promo-view-credits">+{viewCredits}</span>}
    </div>
  );
};

interface HistoryActionsProps {
  onSelect: () => void;
  onTogglePlay: () => void;
  playing: boolean;
  selectable: boolean;
  selected: boolean;
  selectionDisabled: boolean;
  share: ShareItem;
}

const HistoryActions: React.FC<HistoryActionsProps> = (props) => (
  <div className="promo-share-actions">
    {props.share.reel_url && (
      <a className="promo-history-action" href={props.share.reel_url} rel="noreferrer" target="_blank">View post</a>
    )}
    {props.share.video_url && (
      <button className="promo-history-action" onClick={props.onTogglePlay} type="button">
        {props.playing ? 'Hide reel' : 'Preview reel'}
      </button>
    )}
    {props.selectable && (
      <button
        className="promo-history-action promo-history-action--primary"
        disabled={props.selectionDisabled || props.selected}
        onClick={props.onSelect}
        type="button"
      >
        {props.selected ? 'Selected' : 'Select to submit'}
      </button>
    )}
  </div>
);

interface ShareRowProps extends ViewClaimControlProps {
  onSelect: (share: ShareItem) => void;
  selected: boolean;
  selectionDisabled: boolean;
}

const ShareRow: React.FC<ShareRowProps> = (props) => {
  const [playing, setPlaying] = useState(false);
  const posted = Boolean(props.share.posted_at || props.share.reel_url);
  const selectable = props.share.status === 'video_ready' && Boolean(props.share.video_url) && !posted;

  return (
    <div className={`promo-share-card${props.selected ? ' promo-share-card--selected' : ''}`}>
      <HistoryRowHeader posted={posted} share={props.share} />
      <HistoryActions
        onSelect={() => props.onSelect(props.share)}
        onTogglePlay={() => setPlaying((value) => !value)}
        playing={playing}
        selectable={selectable}
        selected={props.selected}
        selectionDisabled={props.selectionDisabled}
        share={props.share}
      />
      {playing && props.share.video_url && (
        <PortraitReelPreview
          label={`${props.share.project_name || 'Project'} reel preview`}
          src={toAbsoluteUrl(props.share.video_url)}
          variant="history"
        />
      )}
      {posted && (
        <ViewClaimControl
          installed={props.installed}
          isSystemAdmin={props.isSystemAdmin}
          refresh={props.refresh}
          share={props.share}
        />
      )}
    </div>
  );
};

interface SharesListProps {
  installed: boolean;
  isSystemAdmin: boolean;
  onSelect: (share: ShareItem) => void;
  refresh: () => void;
  selectedShareId: string | null;
  selectionDisabled: boolean;
  shares: ShareItem[];
}

export const ReelsLoadingState: React.FC = () => (
  <div aria-live="polite" className="promo-reels-loading" role="status">
    <div className="promo-reels-loading-title">
      <span>Loading your reels</span>
      <span className="promo-reels-loading-dot" />
    </div>
    {[0, 1, 2].map((row) => (
      <div className="promo-reel-skeleton" key={row}>
        <span className="promo-reel-skeleton-name" />
        <span className="promo-reel-skeleton-status" />
      </div>
    ))}
  </div>
);

export const SharesList: React.FC<SharesListProps> = (props) => (
  <div className="promo-shares">
    <div className="promo-shares-header">
      <span className="promo-shares-title">Your reels</span>
      <span className="promo-shares-total">{props.shares.length}</span>
    </div>
    <div aria-label="Your reels" className="promo-shares-scroll" role="region" tabIndex={0}>
      {props.shares.map((share) => (
        <ShareRow
          installed={props.installed}
          isSystemAdmin={props.isSystemAdmin}
          key={share.id}
          onSelect={props.onSelect}
          refresh={props.refresh}
          selected={props.selectedShareId === share.id}
          selectionDisabled={props.selectionDisabled}
          share={share}
        />
      ))}
    </div>
  </div>
);

export const ShareWorkspaceSwitcher: React.FC<{
  active: ShareWorkspace;
  count: number;
  onChange: (workspace: ShareWorkspace) => void;
}> = ({ active, count, onChange }) => (
  <div aria-label="Share workspace" className="promo-workspace-switcher" role="group">
    <button
      aria-pressed={active === 'create'}
      className={active === 'create' ? 'active' : ''}
      onClick={() => onChange('create')}
      type="button"
    >
      New reel
    </button>
    <button
      aria-label={`My reels, ${count}`}
      aria-pressed={active === 'history'}
      className={active === 'history' ? 'active' : ''}
      onClick={() => onChange('history')}
      type="button"
    >
      My reels <span aria-hidden="true">{count}</span>
    </button>
  </div>
);

interface ShareHistoryViewProps extends Omit<SharesListProps, 'shares'> {
  info: ShareInfo | null;
}

export const ShareHistoryView: React.FC<ShareHistoryViewProps> = ({ info, ...listProps }) => (
  <div className="promo-history-view">
    {!info ? <ReelsLoadingState /> : info.shares.length === 0 ? (
      <div className="promo-history-empty">
        <strong>No reels yet</strong>
        <span>Create your first project reel to see it here.</span>
      </div>
    ) : <SharesList {...listProps} shares={info.shares} />}
  </div>
);
